import { useFileUpload } from "@msuite/ui/file-upload";
import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardNull } from "@msuite/utils/misc";
import { type TicketFormSchema, ticketSchema } from "@msuite/utils/schemas";
import type { Tables } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { useUpload } from "@/api/files";
import { TicketForm } from "@/forms/ticket-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditTicketModalProps extends ModalProps {
	projectId: string;
	ticketId: string;
	onCompleteEdit?: () => Promise<void> | void;
}

export function EditTicketModal({
	isOpen,
	onClose,
	projectId,
	ticketId,
	onCompleteEdit,
}: EditTicketModalProps) {
	/** Hooks */
	const qc = useQueryClient();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.tickets[":ticketId"].$get({
			param: {
				ticketId,
			},
		});
		const data = await responseHandler(response);
		// The route answers 404 for a ticket that is not the caller's, and `responseHandler` throws
		// on that — so a body that arrives here always carries one, and the failure lands in
		// `loadFailed` rather than in an empty form.
		const ticket = data.ticket as unknown as TicketFormSchema;
		ticket.assignee = data.ticket.assigneeMember || guardNull(data.ticket.assigneePartnership);
		ticket.dateMode = data.ticket.dateMode ?? "date";
		return ticket;
	});
	const methods = useForm({
		defaultValues,
		resolver: resolver(ticketSchema),
	});
	const { t } = useTranslation();
	const { uploadFn } = useUpload();
	const controller = useFileUpload({
		uploadFn,
		defaultFiles: async () => {
			const response = await client.tickets[":ticketId"].files.$get({
				param: {
					ticketId,
				},
			});
			const data = await responseHandler(response);
			return (data.files || []) as Tables<"files">[];
		},
	});

	/** Functions */
	async function handleOnSubmit(json: TicketFormSchema) {
		try {
			const uploadResults = await controller.handleOnFileUpload();
			const response = await client.tickets[":ticketId"].$patch({
				json: {
					uploadResults,
					...json,
				},
				param: {
					ticketId,
				},
			});
			await responseHandler(response);
			await onCompleteEdit?.();
			await qc.invalidateQueries({
				predicate: invalidate([ticketId, projectId]),
			});
			handleOnClose();
		} catch (error) {
			console.error(error);
		}
	}

	function handleOnClose() {
		onClose();
		methods.reset();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isLoading={methods.formState.isLoading && !loadFailed}
				isOpen={isOpen}
				onClose={handleOnClose}
				title={t("projects.edit_ticket")}
				onSubmit={methods.handleSubmit(handleOnSubmit, (error) => {
					console.error(error);
				})}
				data-cy="ticket-form"
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={handleOnClose} />
				) : (
					<>
						<Modal.Body>
							<TicketForm
								controller={controller}
								ticketId={ticketId}
							/>
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
